<script>
    // 세트 일괄 생성 패널. [기본 이미지]·[캐릭터]·[맵] 탭이 같은 것을 쓴다(레시피만 다르다).
    //
    // ⚠️ **지금은 관리자 전용이다.** 일반 플랜은 하루 1~3장이라 11장짜리 세트가 두 번째 장에서
    //    막힌다 — 버튼을 보여주면 "눌렀더니 실패"가 된다. 서버는 어차피 플랜 한도로 막으므로
    //    여기 숨김은 **UI 예의**이지 보안 장치가 아니다.
    //    ⏳ 포인트(크레딧) 제도가 들어오면 이 조건을 "포인트 충분"으로 바꾸면 된다.
    import { onMount, onDestroy, createEventDispatcher } from "svelte";
    import { Button } from "../../lib/ui";
    import { planStore } from "../store/plan-store";
    import ImagePicker from "../chat-builder/ImagePicker.svelte";
    import { isRasterAsset } from "../chat-builder/imageAssetApi.js";
    import { runAssetSetBatch } from "./assetSetBatch.js";

    export let chatId;
    export let steps = [];
    export let folderPath = "";
    /** 패널 제목 옆 설명 */
    export let scopeLabel = "이 게임";

    const dispatch = createEventDispatcher();

    let authType = "";
    let unsub;
    onMount(() => {
        unsub = planStore.subscribe((m) => (authType = m?.authType || ""));
    });
    onDestroy(() => unsub?.());

    $: isAdmin = String(authType).toUpperCase() === "ADMIN";

    let open = false;
    let style = "";
    // 세트 **바깥** 참조 그림. 뿌리 장들이 이걸 보고 만들어져 세트 전체가 화풍을 물려받는다.
    // (세트 안의 연쇄 — 플레이어→탄환, 적→피격/사망 — 는 이것과 별개로 그대로 돈다)
    let refUrl = "";
    let refPickerOpen = false;
    let refError = "";
    let running = false;
    let cancelled = false;
    let progress = []; // [{label, status, error}]
    let summary = "";

    const start = async () => {
        running = true;
        cancelled = false;
        summary = "";
        progress = steps.map((s) => ({ label: s.label, status: "wait" }));

        const { done, failed } = await runAssetSetBatch({
            chatId,
            steps,
            style: style.trim(),
            baseRef: refUrl || undefined,
            folderPath,
            isCancelled: () => cancelled,
            onProgress: (p) => {
                progress[p.index] = { label: p.step.label, status: p.status, error: p.error };
                progress = progress;
                // 한 장 끝날 때마다 바로 올린다 — 중간에 끊겨도 만든 것이 날아가지 않게.
                if (p.status === "done") {
                    dispatch("generated", { slot: p.step.slot, assetId: p.assetId, url: p.url });
                }
            },
        });

        running = false;
        const madeCount = Object.keys(done).length;
        summary = cancelled
            ? `중단했습니다. ${madeCount}장은 만들어져 있어요.`
            : failed.length
              ? `${madeCount}장 완료 · ${failed.length}장 실패`
              : `${madeCount}장 모두 완료`;
    };
</script>

{#if isAdmin}
    <div class="tw-rounded-xl tw-border tw-border-primary/30 tw-bg-primary/5 tw-p-4">
        <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <span class="tw-text-sm tw-font-bold tw-text-foreground">✨ 세트 일괄 생성</span>
            <span class="tw-rounded-full tw-bg-muted tw-px-2 tw-py-0.5 tw-text-[11px] tw-text-muted-foreground">
                관리자 전용
            </span>
            <span class="spacer tw-flex-1"></span>
            {#if !open}
                <Button variant="outline" size="sm" on:click={() => (open = true)}>
                    {steps.length}장 한 번에 만들기
                </Button>
            {/if}
        </div>

        {#if open}
            <p class="tw-mt-2 tw-text-[13px] tw-leading-relaxed tw-text-muted-foreground">
                {scopeLabel}의 이미지 <strong class="tw-text-foreground">{steps.length}장</strong>을 순서대로 만듭니다.
                적의 맞을 때·죽을 때 모습은 <strong class="tw-text-foreground">그 적의 기본 이미지를 참조</strong>해서
                같은 적으로 나오게 합니다. 한 장에 15초쯤 걸려요.
            </p>
            <textarea
                class="tw-mt-3 tw-w-full tw-rounded-md tw-border tw-border-border tw-bg-background tw-p-2.5 tw-text-sm tw-text-foreground"
                rows="2"
                placeholder="공통 화풍 (예: 80년대 도트 레트로, 네온 색감 / 수채화풍 동화, 파스텔)"
                bind:value={style}
                disabled={running}
            ></textarea>

            <div class="tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                {#if refUrl}
                    <img crossorigin="anonymous" class="tw-h-12 tw-w-12 tw-rounded-md tw-object-contain" style="background:#0d1430" src={refUrl} alt="" />
                    <span class="tw-text-xs tw-text-foreground">이 그림의 화풍을 이어받아 만듭니다</span>
                    <button
                        type="button"
                        class="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-[11px] tw-text-muted-foreground tw-underline"
                        on:click={() => (refUrl = "")}
                        disabled={running}>참조 빼기</button
                    >
                {:else}
                    <Button variant="outline" size="sm" on:click={() => (refPickerOpen = true)} disabled={running}>
                        참조 그림 고르기
                    </Button>
                    {#if refError}<span class="tw-text-xs tw-text-destructive">{refError}</span>{/if}
                    <span class="tw-text-xs tw-text-muted-foreground">
                        이미 만든 그림을 참조하면 세트가 그 화풍을 따라갑니다 (선택)
                    </span>
                {/if}
            </div>

            {#if progress.length}
                <div class="tw-mt-3 tw-flex tw-flex-wrap tw-gap-1.5">
                    {#each progress as p (p.label)}
                        <span
                            class="tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] {p.status === 'done'
                                ? 'tw-bg-emerald-500/15 tw-text-emerald-600 dark:tw-text-emerald-400'
                                : p.status === 'running'
                                  ? 'tw-bg-primary/15 tw-text-primary'
                                  : p.status === 'error'
                                    ? 'tw-bg-destructive/15 tw-text-destructive'
                                    : 'tw-bg-muted tw-text-muted-foreground'}"
                            title={p.error || ""}
                        >
                            {p.status === "done" ? "✓" : p.status === "error" ? "✕" : p.status === "running" ? "…" : "·"}
                            {p.label}
                        </span>
                    {/each}
                </div>
            {/if}

            {#if summary}
                <div class="tw-mt-2 tw-text-xs tw-text-muted-foreground">{summary}</div>
            {/if}

            <div class="tw-mt-3 tw-flex tw-items-center tw-gap-2">
                {#if running}
                    <Button variant="outline" size="sm" on:click={() => (cancelled = true)}>중단</Button>
                    <span class="tw-text-xs tw-text-muted-foreground">
                        만드는 중… 창을 닫지 말아주세요
                    </span>
                {:else}
                    <Button size="sm" on:click={start} disabled={!style.trim()}>만들기 시작</Button>
                    <Button variant="outline" size="sm" on:click={() => (open = false)}>닫기</Button>
                    <span class="tw-text-xs tw-text-muted-foreground">
                        이미 있는 이미지는 새로 만든 것으로 바뀝니다
                    </span>
                {/if}
            </div>
        {/if}
    </div>
{/if}

<!-- 참조 그림 고르기. 세트 바깥에서 화풍 기준이 될 이미지를 라이브러리에서 고른다. -->
<ImagePicker
    bind:open={refPickerOpen}
    {chatId}
    contextFolder={folderPath}
    mode="single"
    on:select={(e) => {
        refPickerOpen = false;
        // SVG 등은 AI 참조로 못 쓴다 — 11장이 걸린 자리라 고르는 순간 막는다.
        if (!isRasterAsset(e.detail.url)) {
            refError = "SVG 는 참조로 쓸 수 없어요. PNG·JPG·WEBP 를 골라주세요.";
            return;
        }
        refError = "";
        refUrl = e.detail.url;
    }}
/>
